'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import AppLogo from '@/components/ui/AppLogo';

export default function AuthScreen() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 flex-col items-center justify-center p-12">
        {/* Grid decoration */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'linear-gradient(rgba(59,130,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.3) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        {/* Glow */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-md text-center space-y-8">
          <div className="flex items-center justify-center gap-3">
            <AppLogo size={48} />
            <span className="text-2xl font-bold text-white">SeriousLeverage SMS</span>
          </div>

          <div className="space-y-4">
            <h1 className="text-3xl font-bold text-white leading-tight">
              Your business messaging,<br />
              <span className="text-blue-400">fully automated.</span>
            </h1>
            <p className="text-slate-400 text-base leading-relaxed">
              Appointment reminders, missed-call replies, and real-time SMS conversations — all powered by your own Twilio subaccount.
            </p>
          </div>

          {/* Bento mini preview */}
          <div className="grid grid-cols-2 gap-3 text-left">
            {[
              { icon: '📬', label: 'Real-time Inbox', desc: 'Supabase Realtime push' },
              { icon: '⚡', label: 'Auto Reminders', desc: 'Synced with bookings' },
              { icon: '📞', label: 'Missed Call Reply', desc: 'Auto-SMS on no-answer' },
              { icon: '📊', label: 'SMS Analytics', desc: 'Monthly delivery stats' },
            ].map((feat) => (
              <div
                key={`feat-${feat.label}`}
                className="bg-white/5 border border-white/10 rounded-xl p-3"
              >
                <div className="text-lg mb-1">{feat.icon}</div>
                <p className="text-xs font-semibold text-white">{feat.label}</p>
                <p className="text-[11px] text-slate-400">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex flex-col items-center justify-center px-6 py-12 lg:px-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center justify-center gap-2 mb-8 lg:hidden">
            <AppLogo size={36} />
            <span className="text-lg font-bold text-foreground">SeriousLeverage SMS</span>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-secondary rounded-xl p-1 mb-8">
            {(['login', 'signup'] as const).map((t) => (
              <button
                key={`tab-${t}`}
                onClick={() => setTab(t)}
                className={`
                  flex-1 py-2 rounded-lg text-sm font-medium transition-all duration-150
                  ${tab === t ? 'bg-blue-600 text-white shadow' : 'text-muted-foreground hover:text-foreground'}
                `}
              >
                {t === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {tab === 'login' ? <LoginForm /> : <SignUpForm onSuccess={() => setTab('login')} />}
        </div>
      </div>
    </div>
  );
}